/**
 * Reusable chart components for statistics pages.
 * Built with Recharts for modern, responsive visualizations.
 */

/* eslint-disable @typescript-eslint/no-deprecated */
// Cell component is the standard way to color individual elements in Recharts.
// It's marked as deprecated in types but there's no alternative.

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
} from "recharts";

// Design tokens for consistent theming
const COLORS = {
  primary: "#3b82f6",
  secondary: "#8b5cf6",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  muted: "#94a3b8",
  // Chart palette
  palette: [
    "#3b82f6", // blue
    "#8b5cf6", // violet
    "#22c55e", // green
    "#f59e0b", // amber
    "#ef4444", // red
    "#06b6d4", // cyan
    "#ec4899", // pink
    "#84cc16", // lime
  ],
};

function formatValue(value: unknown): string {
  if (typeof value === "number") {
    return value.toLocaleString("fr-FR");
  }
  if (typeof value === "string") {
    return value;
  }
  return "";
}

function getChartColor(entryColor: string | undefined, index: number): string {
  if (entryColor !== undefined) {
    return entryColor;
  }
  const paletteIndex = index % COLORS.palette.length;
  const color = COLORS.palette[paletteIndex];
  return color ?? COLORS.primary;
}

interface DonutChartProps {
  data: Array<{ name: string; value: number; color?: string }>;
  centerLabel?: string;
  centerValue?: string | number;
  size?: number;
  showLegend?: boolean;
}

/**
 * Donut chart with optional center label.
 * Perfect for showing proportions and distributions.
 */
export function DonutChart({
  data,
  centerLabel,
  centerValue,
  size = 200,
  showLegend = true,
}: DonutChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={size * 0.35}
              outerRadius={size * 0.45}
              paddingAngle={2}
              dataKey="value"
              strokeWidth={0}
            >
              {data.map((entry, index) => (
                <Cell key={entry.name} fill={getChartColor(entry.color, index)} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => [formatValue(value), ""]}
              contentStyle={{
                backgroundColor: "var(--color-card, #ffffff)",
                border: "1px solid var(--color-border, #e5e7eb)",
                borderRadius: "8px",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        {(centerLabel ?? centerValue) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {centerValue !== undefined && (
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {typeof centerValue === "number"
                  ? centerValue.toLocaleString("fr-FR")
                  : centerValue}
              </span>
            )}
            {centerLabel && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{centerLabel}</span>
            )}
          </div>
        )}
      </div>
      {showLegend && (
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
          {data.map((entry, index) => (
            <div key={entry.name} className="flex items-center gap-2">
              <div
                className="h-3 w-3 rounded-full"
                style={{
                  backgroundColor: entry.color || COLORS.palette[index % COLORS.palette.length],
                }}
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {entry.name}{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  ({((entry.value / total) * 100).toFixed(0)}%)
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface BarChartProps {
  data: Array<{ name: string; value: number; color?: string }>;
  height?: number;
  horizontal?: boolean;
  showGrid?: boolean;
  color?: string;
}

/**
 * Bar chart for comparisons.
 */
export function SimpleBarChart({
  data,
  height = 300,
  horizontal = false,
  showGrid = true,
}: BarChartProps) {
  if (horizontal) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ left: 20, right: 20 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />}
          <XAxis type="number" tickFormatter={(v: number) => v.toLocaleString("fr-FR")} />
          <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
          <Tooltip
            formatter={(value) => [formatValue(value), ""]}
            contentStyle={{
              backgroundColor: "var(--color-card, #ffffff)",
              border: "1px solid var(--color-border, #e5e7eb)",
              borderRadius: "8px",
            }}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {data.map((entry, index) => (
              <Cell key={entry.name} fill={getChartColor(entry.color, index)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ bottom: 20 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" opacity={0.3} vertical={false} />}
        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={(v: number) => v.toLocaleString("fr-FR")} />
        <Tooltip
          formatter={(value) => [formatValue(value), ""]}
          contentStyle={{
            backgroundColor: "var(--color-card, #ffffff)",
            border: "1px solid var(--color-border, #e5e7eb)",
            borderRadius: "8px",
          }}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {data.map((entry, index) => (
            <Cell key={entry.name} fill={getChartColor(entry.color, index)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

interface TrendChartProps {
  data: Array<{ name: string; value: number; [key: string]: string | number }>;
  height?: number;
  color?: string;
  showArea?: boolean;
  dataKeys?: Array<{ key: string; color: string; name: string }>;
}

/**
 * Line/Area chart for trends over time.
 */
export function TrendChart({
  data,
  height = 250,
  color = COLORS.primary,
  showArea = true,
  dataKeys,
}: TrendChartProps) {
  if (dataKeys && dataKeys.length > 1) {
    // Multi-line chart
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={(v: number) => v.toLocaleString("fr-FR")} />
          <Tooltip
            formatter={(value, name) => {
              const keyInfo = dataKeys.find((k) => k.key === name);
              return [formatValue(value), keyInfo?.name ?? String(name)];
            }}
            contentStyle={{
              backgroundColor: "var(--color-card, #ffffff)",
              border: "1px solid var(--color-border, #e5e7eb)",
              borderRadius: "8px",
            }}
          />
          <Legend />
          {dataKeys.map((dk) => (
            <Line
              key={dk.key}
              type="monotone"
              dataKey={dk.key}
              stroke={dk.color}
              strokeWidth={2}
              dot={false}
              name={dk.name}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (showArea) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={(v: number) => v.toLocaleString("fr-FR")} />
          <Tooltip
            formatter={(value) => [formatValue(value), ""]}
            contentStyle={{
              backgroundColor: "var(--color-card, #ffffff)",
              border: "1px solid var(--color-border, #e5e7eb)",
              borderRadius: "8px",
            }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorValue)"
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} vertical={false} />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tickFormatter={(v: number) => v.toLocaleString("fr-FR")} />
        <Tooltip
          formatter={(value) => [formatValue(value), ""]}
          contentStyle={{
            backgroundColor: "var(--color-card, #ffffff)",
            border: "1px solid var(--color-border, #e5e7eb)",
            borderRadius: "8px",
          }}
        />
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

interface GaugeProps {
  value: number;
  max?: number;
  size?: number;
  color?: string;
  label?: string;
  showPercentage?: boolean;
  thresholds?: { warning: number; danger: number };
}

/**
 * Radial gauge for percentages and progress.
 */
export function Gauge({
  value,
  max = 100,
  size = 120,
  color,
  label,
  showPercentage = true,
  thresholds = { warning: 70, danger: 90 },
}: GaugeProps) {
  const percentage = Math.min((value / max) * 100, 100);

  const getColor = () => {
    if (color) return color;
    if (percentage >= thresholds.danger) return COLORS.danger;
    if (percentage >= thresholds.warning) return COLORS.warning;
    return COLORS.success;
  };

  const gaugeColor = getColor();

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {/* Background arc */}
            <Pie
              data={[{ value: 100 }]}
              cx="50%"
              cy="50%"
              innerRadius={size * 0.35}
              outerRadius={size * 0.42}
              startAngle={180}
              endAngle={0}
              dataKey="value"
              strokeWidth={0}
            >
              <Cell fill="#e5e7eb" />
            </Pie>
            {/* Value arc */}
            <Pie
              data={[{ value: percentage }, { value: 100 - percentage }]}
              cx="50%"
              cy="50%"
              innerRadius={size * 0.35}
              outerRadius={size * 0.42}
              startAngle={180}
              endAngle={0}
              dataKey="value"
              strokeWidth={0}
            >
              <Cell fill={gaugeColor} />
              <Cell fill="transparent" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {showPercentage && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ paddingTop: size * 0.15 }}
          >
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              {percentage.toFixed(0)}%
            </span>
          </div>
        )}
      </div>
      {label && <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>}
    </div>
  );
}

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  showArea?: boolean;
}

/**
 * Compact sparkline for inline trend visualization.
 */
export function Sparkline({
  data,
  width = 100,
  height = 30,
  color = COLORS.primary,
  showArea = true,
}: SparklineProps) {
  const chartData = data.map((value, index) => ({ index, value }));

  return (
    <ResponsiveContainer width={width} height={height}>
      {showArea ? (
        <AreaChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <defs>
            <linearGradient id={`spark-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#spark-${color.replace("#", "")})`}
          />
        </AreaChart>
      ) : (
        <LineChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={1.5} dot={false} />
        </LineChart>
      )}
    </ResponsiveContainer>
  );
}

interface ProgressBarProps {
  value: number;
  max?: number;
  color?: string;
  showLabel?: boolean;
  size?: "sm" | "md" | "lg";
  thresholds?: { warning: number; danger: number };
}

/**
 * Horizontal progress bar with optional thresholds.
 */
export function ProgressBar({
  value,
  max = 100,
  color,
  showLabel = true,
  size = "md",
  thresholds = { warning: 70, danger: 90 },
}: ProgressBarProps) {
  const percentage = Math.min((value / max) * 100, 100);

  const getColor = () => {
    if (color) return color;
    if (percentage >= thresholds.danger) return COLORS.danger;
    if (percentage >= thresholds.warning) return COLORS.warning;
    return COLORS.success;
  };

  const heights = { sm: "h-1.5", md: "h-2.5", lg: "h-4" };

  return (
    <div className="w-full">
      <div
        className={`w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700 ${heights[size]}`}
      >
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${String(percentage)}%`, backgroundColor: getColor() }}
        />
      </div>
      {showLabel && (
        <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{value.toLocaleString("fr-FR")}</span>
          <span>{percentage.toFixed(0)}%</span>
        </div>
      )}
    </div>
  );
}

interface StatTileProps {
  label: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: React.ReactNode;
  sparklineData?: number[];
  color?: "blue" | "green" | "purple" | "amber" | "red" | "gray";
}

/**
 * Stat tile with optional sparkline and change indicator.
 */
export function StatTile({
  label,
  value,
  change,
  changeLabel,
  icon,
  sparklineData,
  color = "blue",
}: StatTileProps) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400",
    green: "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400",
    purple: "bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400",
    red: "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400",
    gray: "bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  };

  const sparkColors = {
    blue: "#3b82f6",
    green: "#22c55e",
    purple: "#8b5cf6",
    amber: "#f59e0b",
    red: "#ef4444",
    gray: "#6b7280",
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            {icon && (
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${colorClasses[color]}`}
              >
                {icon}
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                {typeof value === "number" ? value.toLocaleString("fr-FR") : value}
              </p>
            </div>
          </div>
          {change !== undefined && (
            <div className="mt-3 flex items-center gap-1">
              <span
                className={`text-sm font-medium ${
                  change > 0
                    ? "text-green-600 dark:text-green-400"
                    : change < 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-gray-500"
                }`}
              >
                {change > 0 ? "+" : ""}
                {change}%
              </span>
              {changeLabel && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{changeLabel}</span>
              )}
            </div>
          )}
        </div>
        {sparklineData && sparklineData.length > 0 && (
          <div className="ml-4">
            <Sparkline data={sparklineData} color={sparkColors[color]} />
          </div>
        )}
      </div>
    </div>
  );
}

export { COLORS };
